/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 콜아웃 블록을 정의한다.
 *
 * @file /src/rootin/blocks/callout/Callout.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 9.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Text } from '@rootin/elements/text/Text';
import { Container } from '@rootin/elements/container/Container';
import { Language, LanguageText } from '@utils/Language';
import './Callout.css';

export class Callout extends Block {
    /**
     * 블록타입
     */
    static type = 'callout';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'atomic';

    /**
     * 블록아이콘
     */
    static icon: string =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-square-menu-icon lucide-square-menu"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M7 8h10"/><path d="M7 12h10"/><path d="M7 16h10"/></svg>';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.callout.label');

    /**
     * 제목 엘리먼트
     */
    title: Text;

    /**
     * 본문 컨테이너
     */
    body: Container;

    /**
     * 콜아웃 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     */
    constructor(editor: Editor) {
        super(editor);
        this.title = new Text(this, {
            name: 'title',
            placeholder: Language.get('rootin.blocks.callout.title.placeholder'),
        });
        this.$getBlock().append(this.title.$getElement());

        this.body = new Container(this, {
            name: 'body',
            blocks: ['paragraph', 'heading', 'images', 'quote'],
            defaultBlock: () => {
                return this.editor
                    .getBlockClass('paragraph')
                    .create(this.editor, { placeholder: Language.get('rootin.blocks.callout.body.placeholder') });
            },
        });
        this.$getBlock().append(this.body.$getElement());
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        await this.title.render();
        await this.body.render();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.title.isEmpty() && this.body.isEmpty();
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        return {
            id: this.getId(),
            type: this.getType(),
            title: this.title.getValue(),
            body: this.body.getValue(),
        };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        await this.title.setValue(value.title);
        await this.body.setValue(value.body);
    }

    /**
     * 블록의 HTML 을 받아, 블록값을 지정한다.
     *
     * @param html - 블록 HTML
     */
    async setValueFromHtml(html: string): Promise<void> {
        const dom = document.createElement('div');
        dom.innerHTML = html;

        const $rootinBlock = dom.firstElementChild;
        if (
            $rootinBlock === null ||
            $rootinBlock.tagName !== 'ROOTIN-BLOCK' ||
            $rootinBlock.getAttribute('data-type') !== this.getType()
        ) {
            return;
        }

        const $title = Array.from($rootinBlock.children).find(
            (el) => el.tagName === 'ROOTIN-ELEMENT' && el.getAttribute('data-type') === 'text'
        );
        if ($title !== undefined) {
            this.title.$getElement().html($title.innerHTML);
        }

        const $body = Array.from($rootinBlock.children).find(
            (el) => el.tagName === 'ROOTIN-ELEMENT' && el.getAttribute('data-type') === 'container'
        );
        if ($body !== undefined) {
            await this.body.setValueFromHtml($body);
        }
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {
        this.title.focus();
    }

    /**
     * 블록내에서 Enter 키입력이 발생했을 때 이벤트를 처리한다.
     *
     * @param element - 실제로 Enter 키입력이 발생한 엘리먼트 (NULL 이면 블록자체)
     */
    onEnter(element: Element | null): void {
        if (element.name == 'title') {
            this.editor.caret.insertLineBreak();
        }
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param focused - Element 인 경우 나의 블록의 포커스된 엘리먼트, `Block`인 경우 새 블록을 삽입할 기준블록
     * @returns 처리결과 (true 인 경우 모든 데이터를 처리함, 그렇지 않은 경우 처리되지 않은 데이터)
     */
    async onPaste(type: string, data: any, focused: Block | Element | null = null): Promise<true | any> {
        if (type.startsWith('text/') === false) {
            return data;
        }

        if (focused === null) {
            return data;
        }

        if (type === 'text/html') {
            const dom = document.createElement('div');
            dom.innerHTML = data as string;

            const $matched = dom.querySelector(`rootin-block[data-type='${this.getType()}']`);
            if ($matched !== null) {
                $matched.remove();
                const remain = dom.innerHTML === '' ? true : dom.innerHTML;

                if (focused instanceof Element) {
                    const container = this.getParent();
                    if (container === null) {
                        return data;
                    }

                    const newBlock = Callout.create(this.editor);
                    await newBlock.setValueFromHtml($matched.outerHTML);
                    await container.insertBlock(newBlock, this.$getBlock().getEl().nextSibling);
                } else {
                    const container = focused.getParent();
                    if (container === null) {
                        return data;
                    }

                    await this.setValueFromHtml($matched.outerHTML);
                    await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);
                }

                return remain;
            }
        }

        if (focused instanceof Element) {
            if (focused instanceof Text) {
                return (await focused.onPasteFirstText(type, data, Text.genericWrapperTags)).remain;
            }
            return data;
        }

        return data;
    }
}
